<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { getTokenForUrl } from '@/utils/tokens'
import type { PoemAttributes } from '../types/types'

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY

const captchaId = ref('')
const token = ref('')

const props = defineProps<{
  location: { x: number; y: number; ID: string } | null
  existingData?: PoemAttributes | null
}>()

const emit = defineEmits(['close', 'save-success', 'delete-success'])
const poemName = ref('')
const mainThought = ref('')
const userName = ref('')
const userAge = ref('')
const isSelectingLocation = ref(false)
const objectId = ref<number | null>(null)
const isEditing = computed(() => objectId.value !== null)
const isConfirmingDelete = ref(false)

const SessionMarkerTimes = []
//Tlačítka

const handleMarkerClick = (event: MouseEvent) => {
  event.stopPropagation()

  isSelectingLocation.value = true

  window.addEventListener('click', handleGlobalClick)
}
//nová metoda kontrolujicí jestli uživatel klikl na mapu, pokud ne tak resetuje marker
//vypomoc s ai
const handleGlobalClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement

  if (target.closest('arcgis-map')) {
    return
  }

  resetMarkerMode()
}

const resetMarkerMode = () => {
  isSelectingLocation.value = false
  window.removeEventListener('click', handleGlobalClick)
  console.log('Marker mode deactivated.')
}
//hlidani pokud se v gismap změní vybraná lokace, pokud ano tak se zresetuje marker
watch(
  () => props.location,
  () => {
    if (isSelectingLocation.value) {
      resetMarkerMode()
    }
  },
)
watch(isSelectingLocation, (active) => {
  if (active) {
    document.body.classList.add('marker-cursor-active')
  } else {
    document.body.classList.remove('marker-cursor-active')
  }
})
watch(
  () => props.existingData,
  (newData) => {
    if (newData) {
      poemName.value = newData.NAZEV || ''
      mainThought.value = newData.POPIS || ''
      userName.value = newData.PREZDIVKA || ''
      userAge.value = newData.VEK ? String(newData.VEK) : ''
      objectId.value = newData.OBJECTID || null
    } else {
      // Reset if it's a completely new point
      poemName.value = ''
      mainThought.value = ''
      userName.value = ''
      userAge.value = ''
      objectId.value = null
    }
  },
  { immediate: true },
)
const cleanUpFunction = () => {
  window.removeEventListener('click', handleGlobalClick)
  document.body.classList.remove('marker-cursor-active')
  isSelectingLocation.value = false
}
const handleDelete = async () => {
  if (!isConfirmingDelete.value) {
    isConfirmingDelete.value = true
    setTimeout(() => {
      isConfirmingDelete.value = false
    }, 2000)
  } else {
    isConfirmingDelete.value = false

    if (!objectId.value) return

    const featureLayerUrl = `https://gp-dc.iprpraha.cz/arcgis/rest/services/app_agendy/praha_poeticka/FeatureServer/0/deleteFeatures`

    const formData = new URLSearchParams()
    formData.append('objectIds', objectId.value.toString())
    formData.append('f', 'json')
    formData.append('token', getTokenForUrl(featureLayerUrl) || '')
    try {
      const response = await fetch(featureLayerUrl, {
        method: 'POST',
        body: formData,
      })

      const result = await response.json()

      if (result.deleteResults && result.deleteResults[0].success) {
        console.log('Successfully deleted from ArcGIS!')

        emit('delete-success', {
          idsezeni: props.location?.ID,
          objectId: objectId.value,
        })
      } else {
        console.error('ArcGIS returned an error:', result)
        alert('Došlo k chybě při mazání.')
      }
    } catch (error) {
      console.error('Network error while deleting:', error)
      alert('Chyba sítě.')
    }
  }
}
onUnmounted(cleanUpFunction)
onMounted(() => {
  initTurnstile()
})

const handleSubmit = async () => {
  if (!props.location || Object.keys(props.location).length === 0) {
    alert('Prosím, nejdříve vyberte místo na mapě pomocí ikony.')
    return
  }

  if (!poemName.value.trim() || !mainThought.value.trim()) {
    alert('Prosím, vyplňte název a popis.')
    return
  }

  if (parseInt(userAge.value) < 15 && userAge.value) {
    alert('Aplikace je určena pro osoby starší 15 let.')
    return
  }
  // @ts-expect-error turnstile doesn't have types
  const isExpired = window.turnstile.isExpired(captchaId.value)
  if (isExpired) {
    // @ts-expect-error turnstile doesn't have types
    window.turnstile.reset(captchaId.value)
  }
  // @ts-expect-error turnstile doesn't have types
  const responseToken = window.turnstile.getResponse(captchaId.value)
  if (!responseToken) {
    return
  }
  token.value = responseToken
  const tokenValidated = await validateToken()
  if (!tokenValidated.success) return

  const attributes: any = {
    NAZEV: poemName.value,
    POPIS: mainThought.value,
    STATUS: 0,
    DATUMEDITACE: Date.now(),
    AUTOR: userName.value,
    EDITOR: userName.value,
    PREZDIVKA: userName.value,
    VEK: userAge.value ? parseInt(userAge.value, 10) : null,
    IDSEZENI: props.location.ID,
  }

  if (objectId.value) {
    attributes.OBJECTID = objectId.value
  } else {
    attributes.DATUMVZNIKU = Date.now()
  }
  const feature = {
    geometry: {
      x: props.location.x,
      y: props.location.y,
      z: 0,
      spatialReference: { wkid: 102067 },
    },
    attributes: attributes,
  }
  const actionType = objectId.value ? 'updateFeatures' : 'addFeatures'
  const featureLayerUrl = `https://gp-dc.iprpraha.cz/arcgis/rest/services/app_agendy/praha_poeticka/FeatureServer/0/${actionType}`

  const formData = new URLSearchParams()
  formData.append('features', JSON.stringify([feature]))
  formData.append('f', 'json')
  formData.append('token', getTokenForUrl(featureLayerUrl) || '')

  try {
    const response = await fetch(featureLayerUrl, {
      method: 'POST',
      body: formData,
    })

    const result = await response.json()
    const actionResults = objectId.value ? result.updateResults : result.addResults

    if (actionResults && actionResults[0].success) {
      console.log(
        `Successfully ${objectId.value ? 'updated' : 'saved'} to ArcGIS!`,
        actionResults[0].objectId,
      )

      if (!objectId.value) {
        SessionMarkerTimes.push(attributes.DATUMVZNIKU)
        poemName.value = ''
        mainThought.value = ''
        // userName.value = ''
        // userAge.value = ''
      }

      // Emit success so App.vue can tell GISMap to update the local point
      emit('save-success', {
        idsezeni: props.location.ID,
        objectId: actionResults[0].objectId,
        attributes: attributes,
      })
      token.value = ''
      // @ts-expect-error turnstile doesn't have types
      window.turnstile.reset(captchaId.value)
    } else {
      console.error('ArcGIS returned an error:', result)
      alert('Došlo k chybě při ukládání.')
      token.value = ''
      // @ts-expect-error turnstile doesn't have types
      window.turnstile.reset(captchaId.value)
    }
  } catch (error) {
    console.error('Network error while saving:', error)
    alert('Chyba sítě.')
    token.value = ''
    // @ts-expect-error turnstile doesn't have types
    window.turnstile.reset(captchaId.value)
  }
}

function initTurnstile() {
  setTimeout(() => {
    // @ts-expect-error turnstile doesn't have types
    captchaId.value = window.turnstile.render('#turnstile-container', {
      sitekey: SITE_KEY,
      callback: (cToken: string) => {
        token.value = cToken
      },
      'error-callback': () => {
        token.value = ''
        // @ts-expect-error turnstile doesn't have types
        window.turnstile.reset(captchaId.value)
      },
      'expired-callback': () => {
        token.value = ''
        // @ts-expect-error turnstile doesn't have types
        window.turnstile.reset(captchaId.value)
      },
    })
  }, 100)
}

async function validateToken() {
  const formData = new FormData()
  formData.append('token', token.value)

  try {
    const response = await fetch('https://geoportalpraha.cz/backend/validate-turnstile-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token: token.value,
      }),
    })

    const result = await response.json()
    return result
  } catch (error) {
    console.error('Turnstile validation error:', error)
    return { success: false, 'error-codes': ['internal-error'] }
  }
}
</script>

<template>
  <div class="add-place-dropdown">
    <button class="close-btn" @click="emit('close')">&times;</button>

    <div class="dropdown-content">
      <!--<button class="how-it-works-btn" @click="handleHowItWorks">Jak to Funguje?</button>-->

      <div class="input-group location-group">
        <input
          type="text"
          placeholder="Název"
          v-model="poemName"
          class="text-input"
          maxlength="100"
        />
        <button
          v-if="!isEditing"
          class="marker-btn"
          :class="{ 'is-active': isSelectingLocation }"
          @click="handleMarkerClick"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            width="20"
            height="20"
          >
            <path
              fill-rule="evenodd"
              d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 00-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.145.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <textarea
        placeholder="Stručný popis místa"
        v-model="mainThought"
        class="main-textarea"
        maxlength="300"
      ></textarea>

      <div class="input-group info-group">
        <input
          type="text"
          placeholder="Jak Vám máme říkat?"
          v-model="userName"
          class="text-input border-right"
          maxlength="255"
        />
        <input
          type="text"
          placeholder="Váš věk?"
          v-model="userAge"
          @input="userAge = userAge.replace(/[^0-9]/g, '')"
          class="text-input age-input"
          maxlength="3"
        />
      </div>

      <div id="turnstile-container"></div>
      <div class="button-group">
        <button
          class="primary-orange-btn submit-btn"
          @click="handleSubmit"
          :disabled="
            !props.location ||
            Object.keys(props.location).length === 0 ||
            !poemName.trim() ||
            !mainThought.trim()
          "
        >
          {{ isEditing ? 'Uložit úpravy' : 'Přidejte poetické místo' }}
        </button>
        <button
          v-if="isEditing"
          class="delete-btn"
          :class="{ 'is-confirming': isConfirmingDelete }"
          @click="handleDelete"
        >
          {{ isConfirmingDelete ? 'Opravdu?' : 'Odstranit' }}
        </button>
      </div>

      <p class="disclaimer">
        Odesláním souhlasíte se zveřejněním příspěvku, vyplněné přezdívky a věku. Vaši IP adresy
        neukládáme. Aplikace je určena pro osoby starší 15 let.
      </p>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500&family=Newsreader:opsz,wght@6..72,400;700&display=swap');
/* Add min-width: 0 to prevent the flex children from pushing past their containers */
.info-group .text-input {
  min-width: 0;
}

/* Lock the age input to a fixed width so it never shrinks or grows */
.age-input {
  flex: 0 0 100px; /* Takes exactly 100px of space */
}
.add-place-dropdown {
  position: absolute;
  top: clamp(60px, 6vw, 75px);
  right: clamp(1rem, 3vw, 2rem);
  width: 350px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  padding: 40px 20px 20px 20px;
  z-index: 1000;
  box-sizing: border-box;
  font-family: 'Inter', sans-serif;
  color: #1a2432;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 15px;
  background: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #1a2432;
  padding: 0;
  line-height: 1;
}
.close-btn:hover {
  transform: scale(1.15);
  color: #f2a900;
}
.dropdown-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
}

.how-it-works-btn {
  background: #f2a900;
  color: white;
  border: 1px solid #eca600;
  border-radius: 4px;
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  align-self: flex-start;
}

.input-group {
  display: flex;
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  overflow: hidden;
}

.text-input,
.main-textarea {
  border: none;
  font-size: 14px;
  color: #1a2432;
  outline: none;
  font-family: 'Inter', sans-serif;
}

.text-input {
  height: 40px;
  padding: 0 12px;
  flex-grow: 1;
}

.main-textarea {
  width: 100%;
  height: 120px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  box-sizing: border-box;
  resize: none;
  font-family: 'Inter', serif;
  font-size: 15px;
  line-height: 1.4;
}

.location-group .marker-btn {
  background: transparent;
  border: none;
  border-left: 1px solid #ccc;
  width: 40px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #1a2432;
  transition: all 0.2s ease;
}
.location-group .marker-btn:hover {
  background-color: #f2a900;
  color: white;
}
.location-group .marker-btn.is-active {
  background-color: #f2a900;
  color: white;
}

.border-right {
  border-right: 1px solid #ccc;
}

.primary-orange-btn {
  background: #f2a900;
  color: white;
  border: none;
  border-radius: 2px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  padding: 12px 0;
  margin-top: 5px;
}
.primary-orange-btn:disabled {
  background: #cccccc; /* Grey color */
  color: #888888;
  cursor: not-allowed; /* Changes cursor to a 'stop' sign */
  opacity: 0.7;
}
.button-group {
  display: flex;
  gap: 2px;
  width: 100%;
  margin-top: 5px;
}

/* Make sure the submit button takes up the remaining space and removes its old top margin */
.button-group .submit-btn {
  flex: 1;
  margin-top: 0;
}

.delete-btn {
  background: #ffebe5;
  color: #d32f2f;
  border: 1px solid #ffcdd2;
  border-radius: 2px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  padding: 12px 0; /* Changed padding to 0 on the sides since we are using fixed width */
  width: 110px; /* Lock the width so it never resizes */
  text-align: center; /* Ensure the text stays perfectly centered */
  transition: all 0.2s ease;
}

.delete-btn:hover {
  background: #ffcdd2;
}

.delete-btn.is-confirming {
  background: #d32f2f;
  color: white;
  border-color: #d32f2f;
  font-weight: bold;
}

.delete-btn.is-confirming:hover {
  background: #b71c1c;
}
.disclaimer {
  text-align: center;
  font-size: 9px;
  color: #1a2432;
  opacity: 0.8;
  line-height: 1;
  margin-top: 5px;
}
</style>

<style>
/*body.marker-cursor-active {
  cursor: crosshair !important; 
}*/

body.marker-cursor-active {
  cursor:
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg'  width='32' height='32' viewBox='0 0 24 24' fill='%23F2A900'><path d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'/></svg>")
      16 32,
    auto;
}
</style>
